import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest, debounceTime, filter, first, map, takeUntil } from 'rxjs';
import { Affiliation } from '../model/affiliation.model';
import { ApiState } from '../model/api-state.model';
import AppError from '../model/app-error';
import { AgentQuotationBody, PremiumCalculationBody, PremiumCalculationRestBody, QuotationBody } from '../model/offer';
import { AppState } from '../store';
import * as fromProcess from '../store/process/process.reducer';
import { formatApiAddOns, formatApiPhoneNumber, getFirstArrayValue } from '../utils';
import isAppAgentOrTravelAgency from '../utils/is-agent-or-travel-agency.util';
import { FormsService } from './forms.service';
import { PersistStorageKeys, PersistStorageService } from './persist-storage.service';
import { ProcessService, ProcessType } from './process.service';

const BACKEND_ID_DIFF = 1;
const COUNTER_START = 0;
const COUNTER_INCREMENT = 1;
const DEBOUNCE_TIME = 700;

@Injectable({
  providedIn: 'root',
})
export class PremiumCalculationService implements OnDestroy {
  fetchPremiumCalculationCounter$ = new BehaviorSubject(COUNTER_START);

  offerPackages$ = this.store.select(fromProcess.selectOfferPackages);
  offerSelectedPackage$ = this.store.select(fromProcess.selectOfferSelectedPackage);
  processKind$ = this.store.select(fromProcess.selectProcessKind);
  premiumCalculation$ = this.store.select(fromProcess.selectPremiumCalculation);
  sendBackOfficeData$ = this.store.select(fromProcess.selectSendBackOfficeData);
  rawCustomizeForTheActiveIncluded$ = this.store.select(fromProcess.selectRawCustomizeForTheActiveIncluded);
  rawCustomizeAlcoholIncluded$ = this.store.select(fromProcess.selectRawCustomizeAlcoholIncluded);
  isAppAgentOrTravelAgency = isAppAgentOrTravelAgency();

  ngDestroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private formsService: FormsService,
    private processService: ProcessService,
    private persistStorageService: PersistStorageService,
  ) {}

  get quotationBody$(): Observable<QuotationBody | AgentQuotationBody | null> {
    return combineLatest([
      this.processKind$,
      this.formsService.hydrationState$,
      this.sendBackOfficeData$,
      this.premiumCalculationBody$,
    ]).pipe(
      filter(([, hydrationState]) => hydrationState === ApiState.success),
      map(([processKind, , , premiumCalculationBody]) => {
        switch (processKind) {
          case ProcessType.TRAVELINPL: {
            const { value } = this.formsService.travelInPl;

            const payload: QuotationBody | AgentQuotationBody = {
              ...this.processService.apiPayload,
              path: {
                travelInPL: {
                  travelDates: this.getTravelDates(value),
                  travelersPerBirthYear: this.getTravelersPerBirthYear(value),
                  coverages: {
                    winterSportsHiRisk: this.getWinterSportsHiRisk(value),
                    ...this.getExtremeSports(value),
                  },
                },
              },
              phoneNumber: formatApiPhoneNumber(value.phoneNumber),
              affiliation: this.getAffiliation(),
              ...premiumCalculationBody,
            };

            return payload;
          }
          case ProcessType.TRIPABROAD: {
            const { value } = this.formsService.tripAbroad;

            return {
              ...this.processService.apiPayload,
              path: {
                tripAbroad: {
                  destination: {
                    countryId: (value.destination ?? '').replace(/[a-z]/gi, ''),
                  },
                  travelDates: value.travelDates?.annual
                    ? {
                        dateFrom: value.travelDates.dateFrom ?? '',
                        annual: value.travelDates.annual ?? false,
                        with60DaysReturns: value.travelDates.with60DaysReturns ?? false,
                      }
                    : this.getTravelDates(value),
                  travelersPerBirthYear: this.getTravelersPerBirthYear(value),
                  isPolandIsCurrentResiding: value.isPolandIsCurrentResiding ?? true,
                  coverages: {
                    winterSportsHiRisk: this.getWinterSportsHiRisk(value),
                    ...this.getExtremeSports(value),
                  },
                },
              },
              phoneNumber: formatApiPhoneNumber(value.phoneNumber),
              affiliation: this.getAffiliation(),
              ...premiumCalculationBody,
            };
          }
          case ProcessType.RESIGNATION: {
            const { value } = this.formsService.resignation;

            return {
              ...this.processService.apiPayload,
              path: {
                resignation: {
                  destination: {
                    countryId: (value.destination ?? '').replace(/[a-z]/gi, ''),
                  },
                  travelDates: this.getTravelDates(value),
                  numberOfInsureds: value.numberOfInsureds ?? NaN,
                  totalTravelPrice: value.totalTravelPrice ?? NaN,
                  travelPurchaseDate: value.travelCost ? getFirstArrayValue(value.travelCost)?.travelPurchaseDate ?? '' : '',
                },
              },
              phoneNumber: formatApiPhoneNumber(value.phoneNumber),
              affiliation: this.getAffiliation(),
              ...premiumCalculationBody,
            };
          }
          default: {
            throw new Error('Not implemented');
          }
        }
      }),
    );
  }

  get premiumCalculationBody$(): Observable<PremiumCalculationRestBody | null> {
    return combineLatest([this.premiumCalculation$, this.rawCustomizeForTheActiveIncluded$, this.rawCustomizeAlcoholIncluded$]).pipe(
      first(),
      map(
        ([
          {
            processKind,
            selectedCustomizeAlcoholIncluded,
            selectedCustomizeForTheActiveIncluded,
            selectedAgentSection,
            selectedPackage,
            selectedPackageName,
            discountCode,
            addons,
          },
          rawCustomizeForTheActiveIncluded,
          rawCustomizeAlcoholIncluded,
        ]) => {
          const addOns = formatApiAddOns(
            selectedCustomizeAlcoholIncluded ?? rawCustomizeAlcoholIncluded,
            selectedCustomizeForTheActiveIncluded ?? rawCustomizeForTheActiveIncluded,
            addons,
          );

          const packageCodeName = selectedPackage?.codeName ?? selectedPackageName;

          if (!packageCodeName) {
            return null;
          }

          switch (processKind) {
            case ProcessType.TRAVELINPL: {
              const { value } = this.formsService.travelInPl;

              return {
                ...this.processService.apiPayload,
                selectedOffert: {
                  packageCodeName,
                  addOns,
                },
                insureds: this.formsService.policyholder.controls.insureds.valid ? this.formsService.insuredsApi : undefined,
                discountCode: {
                  code: discountCode ?? '',
                },
                ...(!this.isAppAgentOrTravelAgency ? { phoneNumber: formatApiPhoneNumber(value.phoneNumber) } : {}),
                ...(this.isAppAgentOrTravelAgency &&
                selectedAgentSection?.premiumPercentIncrease !== null &&
                selectedAgentSection?.premiumPercentIncrease !== undefined
                  ? {
                      agentSection: {
                        premiumPercentIncrease: selectedAgentSection?.premiumPercentIncrease ?? null,
                      },
                    }
                  : {}),
              };
            }
            case ProcessType.TRIPABROAD: {
              const { value } = this.formsService.tripAbroad;

              return {
                ...this.processService.apiPayload,
                selectedOffert: {
                  packageCodeName,
                  addOns,
                },
                insureds: this.formsService.policyholder.controls.insureds.valid ? this.formsService.insuredsApi : undefined,
                discountCode: {
                  code: discountCode ?? '',
                },
                ...(!this.isAppAgentOrTravelAgency ? { phoneNumber: formatApiPhoneNumber(value.phoneNumber) } : {}),
                ...(this.isAppAgentOrTravelAgency &&
                selectedAgentSection?.premiumPercentIncrease !== null &&
                selectedAgentSection?.premiumPercentIncrease !== undefined
                  ? {
                      agentSection: {
                        premiumPercentIncrease: selectedAgentSection?.premiumPercentIncrease ?? null,
                      },
                    }
                  : {}),
              };
            }
            case ProcessType.RESIGNATION: {
              const { value } = this.formsService.resignation;

              return {
                ...this.processService.apiPayload,
                selectedOffert: {
                  packageCodeName,
                  addOns,
                },
                insureds: this.formsService.policyholder.controls.insureds.valid ? this.formsService.insuredsApi : undefined,
                discountCode: {
                  code: discountCode ?? '',
                },
                ...(!this.isAppAgentOrTravelAgency ? { phoneNumber: formatApiPhoneNumber(value.phoneNumber) } : {}),
                ...(this.isAppAgentOrTravelAgency &&
                selectedAgentSection?.premiumPercentIncrease !== null &&
                selectedAgentSection?.premiumPercentIncrease !== undefined
                  ? {
                      agentSection: {
                        premiumPercentIncrease: selectedAgentSection?.premiumPercentIncrease ?? null,
                      },
                    }
                  : {}),
              };
            }
            default: {
              throw new Error('Not implemented');
            }
          }
        },
      ),
    );
  }

  getAffiliation(): Affiliation | undefined {
    const affiliateNumber = this.persistStorageService.getString(PersistStorageKeys.AffiliateNumber, 'session');

    return !this.isAppAgentOrTravelAgency && !!affiliateNumber
      ? {
          affiliateNumber,
        }
      : undefined;
  }

  getTravelDates = (
    value: Partial<{
      travelDates: Partial<{
        dateFrom: string | null;
        dateTo: string | null;
      }>;
    }>,
  ) => ({
    dateFrom: value.travelDates?.dateFrom ?? '',
    dateTo: value.travelDates?.dateTo ?? '',
  });

  getTravelersPerBirthYear = (
    value: Partial<{
      numberOfInsureds: number | null;
      insuredBirthYear: (number | null)[];
    }>,
  ) => ({
    numberOfInsureds: value.numberOfInsureds ?? NaN,
    insuredBirthYear:
      value.insuredBirthYear?.map((birthYear, id) => ({
        id: id + BACKEND_ID_DIFF,
        birthYear: birthYear ?? NaN,
      })) ?? [],
  });

  getWinterSportsHiRisk = (
    value: Partial<{
      winterSportsHiRisk: boolean | null;
      winterSportsHiRiskForAllInsureds: boolean | null;
      personsWithWinterSportsHiRisk: (boolean | null)[];
    }>,
  ) => {
    const base = {
      selected: value.winterSportsHiRisk ?? false,
    };

    if (value.winterSportsHiRisk) {
      const winterSportsHiRiskForAllInsureds =
        value.winterSportsHiRiskForAllInsureds || value.personsWithWinterSportsHiRisk?.every((selected) => selected) || false;

      const specifying = {
        winterSportsHiRiskForAllInsureds,
        ...(!winterSportsHiRiskForAllInsureds
          ? {
              personsWithWinterSportsHiRisk: value.personsWithWinterSportsHiRisk?.map((selected, id) => ({
                id: id + BACKEND_ID_DIFF,
                selected: selected ?? false,
              })),
            }
          : {}),
      };

      return { ...base, specifying };
    }

    return base;
  };

  getExtremeSports = (
    value: Partial<{
      withExtremeSports: boolean | null;
      extremeSportsForAllInsureds: boolean | null;
      personsWithExtremeSports: (boolean | null)[];
    }>,
  ) => {
    if (!this.isAppAgentOrTravelAgency) {
      return {};
    }

    const base = {
      selected: value.withExtremeSports ?? false,
    };

    if (value.withExtremeSports) {
      const extremeSportsForAllInsureds =
        value.extremeSportsForAllInsureds || value.personsWithExtremeSports?.every((selected) => selected) || false;

      const specifying = {
        extremeSportsForAllInsureds,
        ...(!extremeSportsForAllInsureds
          ? {
              personsWithExtremeSports: value.personsWithExtremeSports?.map((selected, id) => ({
                id: id + BACKEND_ID_DIFF,
                selected: selected ?? false,
              })),
            }
          : {}),
      };

      return {
        extremeSports: { ...base, specifying },
      };
    }

    return { extremeSports: base };
  };

  fetchPremiumCalculation(): void {
    combineLatest([this.quotationBody$, this.premiumCalculationBody$, this.sendBackOfficeData$, this.offerSelectedPackage$])
      .pipe(first())
      .subscribe(([quotationBody, premiumCalculationBody, sendBackOfficeData, offerSelectedPackage]) => {
        if (!offerSelectedPackage) {
          return;
        }

        if (!quotationBody || !premiumCalculationBody) {
          this.store.dispatch(
            fromProcess.actions.fetchPremiumCalculationError({
              packageName: offerSelectedPackage.codeName,
              error: new AppError('app', null, ''),
            }),
          );

          return;
        }

        let body: PremiumCalculationBody = {
          ...quotationBody,
          ...premiumCalculationBody,
        };

        if (sendBackOfficeData) {
          body = {
            ...body,
            backofficeSection: this.formsService.backOfficeSectionApi,
          };
        }

        this.store.dispatch(
          fromProcess.actions.fetchPremiumCalculationStart({
            body: {
              ...body,
              selectedOffert: {
                ...body.selectedOffert,
                packageCodeName: offerSelectedPackage.codeName,
              },
            },
          }),
        );
      });
  }

  listenPremiumCalculation(): void {
    this.fetchPremiumCalculationCounter$.pipe(takeUntil(this.ngDestroy$), debounceTime(DEBOUNCE_TIME)).subscribe(() => {
      this.fetchPremiumCalculation();
    });
  }

  newPremiumCalculation(): void {
    this.fetchPremiumCalculationCounter$.next(this.fetchPremiumCalculationCounter$.value + COUNTER_INCREMENT);
  }

  ngOnDestroy(): void {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
